import { Link, useLocation } from "react-router-dom";
import { cn } from "@jambushrushti/lib/utils";
import { useAdminAuth } from "@jambushrushti/contexts/AdminAuthContext";
import {
  LayoutDashboard,
  BookOpen,
  Calendar,
  Video,
  FileText,
  LogOut,
  GraduationCap,
  X,
  Award,
  ClipboardList,
  HelpCircle,
  Home,
} from "lucide-react";
import { Button } from "@jambushrushti/components/ui/button";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@jambushrushti/components/ui/avatar";

const studentNavItems = [
  {
    icon: LayoutDashboard,
    label: "Dashboard",
    href: "/learning/student-dashboard",
  },
  { icon: BookOpen, label: "Browse Courses", href: "/learning/courses" },
  { icon: GraduationCap, label: "My Courses", href: "/learning/my-courses" },
  { icon: Calendar, label: "Schedule", href: "/learning/my-schedule" },
  {
    icon: ClipboardList,
    label: "Assignments",
    href: "/learning/my-assignments",
  },
  { icon: HelpCircle, label: "Quizzes", href: "/learning/my-quizzes" },
  { icon: FileText, label: "Materials", href: "/learning/my-materials" },
  { icon: Award, label: "Certificates", href: "/learning/certificates" },
];

interface StudentSidebarProps {
  onClose?: () => void;
}

export function StudentSidebar({ onClose }: StudentSidebarProps) {
  const location = useLocation();
  const { user, logout } = useAdminAuth();

  const handleLogout = async () => {
    await logout();
    window.location.href = "/auth";
  };

  return (
    <aside className="flex min-h-screen w-72 flex-col bg-primary text-primary-foreground lg:w-64">
      {/* Logo/Brand */}
      <div className="flex items-center justify-between border-b border-primary-foreground/10 p-4 sm:p-6">
        <Link
          to="/learning/student-dashboard"
          className="flex items-center gap-3"
          onClick={onClose}
        >
          <div className="bg-orange/20 flex h-10 w-10 items-center justify-center rounded-xl">
            <GraduationCap className="text-orange h-6 w-6" />
          </div>
          <div>
            <h1 className="font-heading text-lg font-bold text-white">
              Learning Portal
            </h1>
            <p className="text-xs text-white/60">Jambushrusti</p>
          </div>
        </Link>
        {onClose && (
          <Button
            variant="ghost"
            size="icon"
            className="text-white/70 hover:bg-white/10 hover:text-white lg:hidden"
            onClick={onClose}
          >
            <X className="h-5 w-5" />
          </Button>
        )}
      </div>

      {/* User Info */}
      <div className="border-b border-primary-foreground/10 p-4">
        <div className="flex items-center gap-3">
          <Avatar className="border-orange/30 h-10 w-10 border-2">
            <AvatarImage src={user?.avatar} />
            <AvatarFallback className="bg-orange/20 text-orange font-semibold">
              {user?.name?.charAt(0).toUpperCase() || "S"}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-white">
              {user?.name || "Student"}
            </p>
            <p className="truncate text-xs text-white/60">
              {user?.email || "student@example.com"}
            </p>
          </div>
        </div>
      </div>

      {/* Role Badge */}
      <div className="px-4 py-2">
        <div className="bg-orange/20 text-orange rounded-lg px-3 py-1.5 text-center text-xs font-medium">
          Student
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 space-y-1 overflow-auto p-3 sm:p-4">
        {studentNavItems.map(item => {
          const isActive =
            location.pathname === item.href ||
            (item.href !== "/learning/student-dashboard" &&
              location.pathname.startsWith(item.href));

          return (
            <Link
              key={item.href}
              to={item.href}
              onClick={onClose}
              className={cn(
                "flex items-center gap-3 rounded-xl px-4 py-3 transition-all duration-200",
                isActive
                  ? "bg-orange font-medium text-white shadow-lg"
                  : "text-white/70 hover:bg-white/10 hover:text-white"
              )}
            >
              <item.icon className="h-5 w-5 flex-shrink-0" />
              <span className="text-sm">{item.label}</span>
            </Link>
          );
        })}
      </nav>

      {/* Back to Main Site */}
      <div className="border-t border-primary-foreground/10 p-4">
        <Link
          to="/"
          onClick={onClose}
          className="flex items-center gap-3 rounded-xl px-4 py-3 text-white/70 transition-all duration-200 hover:bg-white/10 hover:text-white"
        >
          <Home className="h-5 w-5 flex-shrink-0" />
          <span className="text-sm">Back to Main Site</span>
        </Link>
      </div>

      {/* Logout */}
      <div className="border-t border-primary-foreground/10 p-4">
        <Button
          variant="ghost"
          className="w-full justify-start text-white/70 hover:bg-white/10 hover:text-white"
          onClick={handleLogout}
        >
          <LogOut className="mr-3 h-5 w-5" />
          Sign Out
        </Button>
      </div>
    </aside>
  );
}
